import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import {
  acceptConsent,
  denyConsent,
  shouldShowBanner,
  resetConsent,
  OPEN_CONSENT_EVENT,
} from "@/lib/cookieConsent";

/**
 * Sticky bottom cookie-consent banner.
 *
 * Behavior:
 *  - Mounts on first visit (no prior decision recorded) and shows itself.
 *  - "Accept" persists the choice in localStorage (tracking continues).
 *  - "Deny" stores the choice in sessionStorage only (no tracking this
 *    session, but the banner re-appears on the next browser session).
 *  - Clicking ANYWHERE else on the banner (the empty area, the text, the
 *    cookie icon, the close button) is treated as "Accept" — accept is the
 *    default per product requirement. The policy link and Deny button do
 *    NOT trigger accept because their handlers stop propagation.
 *  - Listens for an `open-cookie-consent` window event to re-open on
 *    demand (used by the footer "Manage cookie preferences" link).
 */
export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  // Used to play a tiny mount transition without an extra library.
  const [entered, setEntered] = useState(false);
  const animTimer = useRef(null);

  // Initial visibility check after mount (avoids SSR / hydration issues).
  useEffect(() => {
    if (shouldShowBanner()) setVisible(true);
  }, []);

  // Subscribe to external "open me" events.
  useEffect(() => {
    const onOpen = () => {
      // Clear any prior choice so the banner is meaningful when reopened.
      resetConsent();
      setVisible(true);
    };
    window.addEventListener(OPEN_CONSENT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, onOpen);
  }, []);

  // Slide-in animation when the banner becomes visible.
  useEffect(() => {
    if (!visible) {
      setEntered(false);
      return;
    }
    // Allow the next paint to apply the "translate-y-0" class.
    animTimer.current = window.setTimeout(() => setEntered(true), 20);
    return () => {
      if (animTimer.current) window.clearTimeout(animTimer.current);
    };
  }, [visible]);

  const handleAccept = useCallback(() => {
    acceptConsent();
    setVisible(false);
  }, []);

  const handleDeny = useCallback((e) => {
    // Prevent the click from bubbling to the banner-wide "accept" handler.
    e.stopPropagation();
    denyConsent();
    setVisible(false);
  }, []);

  // Anchor links (policy review) should not trigger banner-wide accept.
  const stop = (e) => e.stopPropagation();

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      onClick={handleAccept}
      className={[
        "fixed inset-x-0 bottom-0 z-[60] cursor-pointer select-none",
        "transition-transform duration-300 ease-out",
        entered ? "translate-y-0" : "translate-y-full",
      ].join(" ")}
    >
      <div className="mx-auto max-w-6xl m-3 sm:m-4">
        <div
          className={[
            "rounded-2xl shadow-2xl ring-1 ring-amber-900/20",
            "bg-stone-900/95 backdrop-blur text-amber-50",
            "px-4 sm:px-6 py-4 sm:py-5",
            "flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5",
          ].join(" ")}
        >
          <div className="flex items-start sm:items-center gap-3 flex-1">
            <div className="shrink-0 w-9 h-9 rounded-xl bg-yellow-400/15 ring-1 ring-yellow-400/30 grid place-items-center">
              <Cookie className="w-5 h-5 text-yellow-300" />
            </div>
            <p className="text-sm leading-relaxed text-stone-100">
              We use cookies and similar tech to measure performance and improve
              your experience.{" "}
              <Link
                to="/cookie-policy"
                onClick={stop}
                className="underline decoration-yellow-300/70 underline-offset-2 hover:text-yellow-200"
                data-testid="cookie-banner-policy-link"
              >
                Review our cookie policy
              </Link>
              .{" "}
              <span className="text-stone-300 hidden sm:inline">
                Click anywhere on this banner to accept.
              </span>
            </p>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 sm:shrink-0">
            <button
              type="button"
              onClick={handleDeny}
              className="px-4 py-2 rounded-full text-sm font-medium text-stone-200 hover:text-white hover:bg-white/10 ring-1 ring-white/15 transition"
              data-testid="cookie-banner-deny"
            >
              Deny
            </button>
            <button
              type="button"
              onClick={(e) => {
                // Explicit accept button — same as banner-wide click.
                e.stopPropagation();
                handleAccept();
              }}
              className="px-4 py-2 rounded-full text-sm font-semibold bg-yellow-400 text-amber-900 hover:bg-yellow-300 transition"
              data-testid="cookie-banner-accept"
            >
              Accept
            </button>
            <button
              type="button"
              aria-label="Dismiss (accept)"
              onClick={handleAccept}
              className="hidden sm:grid place-items-center w-8 h-8 rounded-full text-stone-300 hover:text-white hover:bg-white/10 transition"
              data-testid="cookie-banner-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
